import {
  Calculator,
  Building2,
  Camera,
  CircleCheck,
  ChevronsLeft,
  FileText,
  ChartColumn,
  Gauge,
  HardHat,
  ListTodo,
  Settings,
  Star,
  Trash2,
  TriangleAlert,
  IdCard,
  CalendarClock,
  CalendarDays,
  CircleHelp,
  ReceiptText,
  Target,
  Truck,
  Users,
  ScrollText,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/format';
import { Button, ProgressBar, Tooltip } from '@/components/ui';
import type { CurrentUserDto } from '@obralink/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  /** Permiso necesario; sin él, el elemento NO se renderiza */
  permission?: string;
}

export const MAIN: NavItem[] = [
  { to: '/panel', label: 'Panel', icon: Gauge },
  // ANALÍTICA APARTE DEL PANEL — tarea 8.15. El panel responde «¿qué hago
  // hoy?» y se abre veinte veces al día; esto responde «¿cómo va el negocio?»
  // y se abre una vez a la semana. Juntarlos haría que lo primero tardara y
  // que lo segundo quedara debajo del pliegue.
  { to: '/analitica', label: 'Analítica', icon: ChartColumn, permission: 'report:export' },
  { to: '/obras', label: 'Obras', icon: HardHat },
  { to: '/documentos', label: 'Documentos', icon: FileText, permission: 'file:read' },
  { to: '/incidencias', label: 'Incidencias', icon: TriangleAlert, permission: 'incident:read' },
  { to: '/mis-tareas', label: 'Mis tareas', icon: CircleCheck, permission: 'task:read' },
  { to: '/calendario', label: 'Calendario', icon: CalendarDays },
  { to: '/tareas', label: 'Tareas', icon: ListTodo, permission: 'task:read' },
  { to: '/fotos', label: 'Fotografías', icon: Camera, permission: 'photo:read' },
  // MISMO PERMISO QUE CLIENTES, `client:read`, y no uno nuevo: el embudo es la
  // parte de delante de la ficha de cliente, y quien puede ver a los clientes de
  // la empresa puede ver qué se está ofertando. Inventar aquí un `crm:read` sin
  // darlo a nadie escondería la pantalla para todo el mundo (H.101).
  { to: '/comercial', label: 'Comercial', icon: Target, permission: 'client:read' },
  { to: '/presupuestos', label: 'Presupuestos', icon: Calculator, permission: 'budget:read' },
  {
    to: '/certificaciones',
    label: 'Certificaciones',
    icon: ReceiptText,
    permission: 'certification:read',
  },
  { to: '/clientes', label: 'Clientes', icon: Building2, permission: 'client:read' },
  { to: '/proveedores', label: 'Proveedores', icon: Users, permission: 'supplier:read' },
  { to: '/empleados', label: 'Plantilla', icon: IdCard, permission: 'employee:read' },
  // `asset:read` y no `machinery:read`: ese permiso NO EXISTE en el catálogo,
  // así que esta entrada llevaba oculta para todo el mundo desde que se
  // escribió. Es la misma familia de fallo que H.97 —algo que se comprueba
  // contra un permiso que nadie tiene— y el síntoma es el mismo: no pasa nada,
  // simplemente no está.
  { to: '/maquinaria', label: 'Maquinaria', icon: Truck, permission: 'asset:read' },
  { to: '/vencimientos', label: 'Qué caduca', icon: CalendarClock, permission: 'compliance:read' },
];

export const SECONDARY: NavItem[] = [
  { to: '/favoritos', label: 'Favoritos', icon: Star },
  { to: '/ayuda', label: 'Ayuda', icon: CircleHelp },
  { to: '/papelera', label: 'Papelera', icon: Trash2, permission: 'file:delete' },
];

function NavItemLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const link = (
    <NavLink
      to={item.to}
      data-tour={`nav${item.to}`}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-brand-soft text-brand-soft-fg'
            : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        )
      }
    >
      <item.icon className="size-4.5 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={item.label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

export function Sidebar({
  user,
  collapsed,
  onToggle,
}: {
  user: CurrentUserDto;
  collapsed: boolean;
  onToggle: () => void;
}) {
  // El menú se construye desde los permisos efectivos: lo que el usuario no
  // puede hacer no se le enseña deshabilitado, directamente no existe
  // (plan.md §6, principio 5).
  const can = (p?: string) => !p || user.permissions.includes(p);

  const usedPct = (user.company.storageUsedBytes / user.company.storageQuotaBytes) * 100;

  return (
    <aside
      data-tour="menu-lateral"
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-border bg-surface transition-[width]',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className={cn('flex h-14 items-center gap-2 px-3', collapsed && 'justify-center px-0')}>
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand text-fg-on-brand">
          <HardHat className="size-4.5" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-fg">Obralink</p>
            <p className="truncate text-xs text-fg-muted">{user.company.name}</p>
          </div>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
        {MAIN.filter((i) => can(i.permission)).map((item) => (
          <NavItemLink key={item.to} item={item} collapsed={collapsed} />
        ))}

        <div className="my-3 h-px bg-border" />

        {SECONDARY.filter((i) => can(i.permission)).map((item) => (
          <NavItemLink key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {!collapsed && (
        <div data-tour="almacenamiento" className="border-t border-border px-3 py-3">
          <div className="mb-1.5 flex items-baseline justify-between text-xs">
            <span className="font-medium text-fg-muted">Almacenamiento</span>
            <span className="tabular text-fg-subtle">{Math.round(usedPct)} %</span>
          </div>
          <ProgressBar
            value={usedPct}
            size="sm"
            tone={usedPct > 90 ? 'danger' : usedPct > 75 ? 'warning' : 'brand'}
            label="Almacenamiento usado"
          />
          <p className="mt-1.5 text-xs text-fg-subtle">
            {formatBytes(user.company.storageUsedBytes)} de{' '}
            {formatBytes(user.company.storageQuotaBytes)}
          </p>
        </div>
      )}

      <div className="border-t border-border p-2">
        <div className={cn('flex items-center gap-1', collapsed && 'flex-col')}>
          {can('user:update') && (
            <NavItemLink
              item={{ to: '/usuarios', label: 'Usuarios', icon: Settings }}
              collapsed={collapsed}
            />
          )}
          {/* La auditoría va abajo con los ajustes y no en el menú principal:
              es una pantalla de investigación, no de trabajo diario. Y sólo la
              ve quien tiene `audit:read`, que no es todo el mundo. */}
          {can('audit:read') && (
            <NavItemLink
              item={{ to: '/auditoria', label: 'Auditoría', icon: ScrollText }}
              collapsed={collapsed}
            />
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggle}
            aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
            className={cn('ml-auto', collapsed && 'ml-0')}
          >
            <ChevronsLeft className={cn('transition-transform', collapsed && 'rotate-180')} />
          </Button>
        </div>
      </div>
    </aside>
  );
}
